import React, { useState } from 'react'
import { 
    IconButton, 
    Tooltip, 
    Box, 
    Typography, 
    Chip, 
    Fade,
    Slide,
    CircularProgress
} from '@mui/material'
import { 
    Wifi, 
    WifiOff, 
    Refresh, 
    CheckCircle,
    Error,
    Warning
} from '@mui/icons-material'
import { useWebSocket, useStateUpdates } from '../hooks/useWebSocket'
import { useSelector } from 'react-redux'

const WebSocketNavbarStatus = () => {
    const { connectionStatus, reconnect } = useWebSocket()
    const { lastUpdate, updateCount } = useStateUpdates()
    const isDarkMode = useSelector(state => state.settings.darkMode)
    const [isReconnecting, setIsReconnecting] = useState(false)

    const handleReconnect = async () => {
        setIsReconnecting(true)
        try {
            await reconnect()
        } finally {
            setTimeout(() => setIsReconnecting(false), 1000)
        }
    }

    const getStatusConfig = () => {
        if (connectionStatus.isConnected) {
            return {
                icon: <CheckCircle sx={{ fontSize: 16 }} />,
                label: 'WS',
                color: '#4caf50',
                bgColor: isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
                borderColor: isDarkMode ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)',
                textColor: isDarkMode ? '#81c784' : '#4caf50',
                status: 'Подключен'
            }
        } else if (connectionStatus.reconnectAttempts > 0) {
            return {
                icon: <Warning sx={{ fontSize: 16 }} />,
                label: 'RETRY',
                color: '#ff9800',
                bgColor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)',
                borderColor: isDarkMode ? 'rgba(255, 152, 0, 0.4)' : 'rgba(255, 152, 0, 0.3)',
                textColor: isDarkMode ? '#ffb74d' : '#ff9800',
                status: 'Переподключение'
            }
        } else {
            return {
                icon: <Error sx={{ fontSize: 16 }} />,
                label: 'OFF',
                color: '#f44336',
                bgColor: isDarkMode ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)',
                borderColor: isDarkMode ? 'rgba(244, 67, 54, 0.4)' : 'rgba(244, 67, 54, 0.3)',
                textColor: isDarkMode ? '#e57373' : '#f44336',
                status: 'Отключен'
            }
        }
    }

    const formatLastUpdate = () => {
        if (!lastUpdate) return 'Нет обновлений'
        
        const date = new Date(lastUpdate)
        const now = new Date()
        const diff = Math.floor((now - date) / 1000)
        
        if (diff < 60) {
            return `${diff} сек назад`
        } else if (diff < 3600) {
            return `${Math.floor(diff / 60)} мин назад`
        } else {
            return date.toLocaleTimeString()
        }
    }

    const statusConfig = getStatusConfig()

    const getTooltipContent = () => (
        <Box sx={{ p: 2, minWidth: 280 }}>
            <Typography 
                variant="subtitle2" 
                sx={{ 
                    mb: 2, 
                    fontWeight: 700,
                    color: isDarkMode ? '#ffffff' : '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: statusConfig.color,
                    boxShadow: `0 0 8px ${statusConfig.color}40`
                }} />
                WebSocket Статус
            </Typography>
            
            <Box sx={{ mb: 2 }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    p: 1.5,
                    borderRadius: 1,
                    background: isDarkMode 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.03)',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
                }}>
                    {statusConfig.icon}
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 600,
                            color: statusConfig.textColor
                        }}
                    >
                        {statusConfig.status}
                    </Typography>
                </Box>
            </Box>
            
            {connectionStatus.isConnected && connectionStatus.socketId && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ 
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                        fontWeight: 500
                    }}>
                        ID подключения:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontFamily: 'monospace',
                        color: isDarkMode ? '#ffffff' : '#000000',
                        fontWeight: 600
                    }}>
                        {connectionStatus.socketId.substring(0, 12)}...
                    </Typography>
                </Box>
            )}
            
            {connectionStatus.reconnectAttempts > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ 
                        color: '#ff9800',
                        fontWeight: 500
                    }}>
                        Попыток переподключения:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        color: '#ff9800',
                        fontWeight: 600
                    }}>
                        {connectionStatus.reconnectAttempts}
                    </Typography>
                </Box>
            )}
            
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                borderRadius: 1,
                background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.03)' 
                    : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`
            }}>
                <Box>
                    <Typography variant="caption" sx={{ 
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                        display: 'block'
                    }}>
                        Обновлений получено
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        color: isDarkMode ? '#ffffff' : '#000000',
                        fontWeight: 600
                    }}>
                        {updateCount}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ 
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                        display: 'block'
                    }}>
                        Последнее обновление
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        color: isDarkMode ? '#ffffff' : '#000000',
                        fontWeight: 600
                    }}>
                        {formatLastUpdate()}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )

    return (
        <Tooltip 
            title={getTooltipContent()}
            placement="bottom"
            arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        backgroundColor: isDarkMode ? '#2d2d2d' : '#ffffff',
                        color: isDarkMode ? '#ffffff' : '#000000',
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                        boxShadow: isDarkMode 
                            ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
                            : '0 8px 32px rgba(0, 0, 0, 0.15)',
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                    },
                },
                arrow: {
                    sx: {
                        color: isDarkMode ? '#2d2d2d' : '#ffffff',
                        '&::before': {
                            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                        }
                    }
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Компактный индикатор статуса */}
                <Chip
                    icon={statusConfig.icon}
                    label={statusConfig.label}
                    size="small"
                    variant="outlined"
                    sx={{
                        height: 28,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        background: statusConfig.bgColor,
                        color: statusConfig.textColor,
                        border: `1px solid ${statusConfig.borderColor}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            background: statusConfig.bgColor.replace('0.1', '0.2').replace('0.15', '0.25'),
                            transform: 'scale(1.02)',
                        },
                        '& .MuiChip-icon': {
                            fontSize: '0.8rem',
                            color: statusConfig.textColor,
                        },
                        '& .MuiChip-label': {
                            px: 0.8,
                            fontWeight: 700,
                        },
                    }}
                />
                
                {/* Кнопка переподключения */}
                {!connectionStatus.isConnected && (
                    <Fade in={true} timeout={300}>
                        <Tooltip title="Переподключиться">
                            <IconButton
                                size="small"
                                onClick={handleReconnect}
                                disabled={isReconnecting}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    color: statusConfig.textColor,
                                    background: statusConfig.bgColor,
                                    border: `1px solid ${statusConfig.borderColor}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        background: statusConfig.bgColor.replace('0.1', '0.2').replace('0.15', '0.25'),
                                        transform: 'scale(1.1)',
                                    },
                                    '&:disabled': {
                                        opacity: 0.6,
                                    }
                                }}
                            >
                                {isReconnecting ? (
                                    <CircularProgress size={14} sx={{ color: statusConfig.textColor }} />
                                ) : (
                                    <Refresh sx={{ fontSize: 14 }} />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Fade>
                )}
            </Box>
        </Tooltip>
    )
}

export default WebSocketNavbarStatus
